import { FormattedMessage } from "react-intl";

import BackButton from "@components/buttons/BackButton";
import SecondaryButton from "@components/buttons/SecondaryButton";
import ExpertAvatar from "@components/ExpertAvatar";
import styles from "@components/expertSection/ExpertSection.module.css";
import ScheduleConsultButton from "@components/ScheduleConsultButton";
import BodyCopy from "@components/typography/BodyCopy";
import Heading2 from "@components/typography/Heading2";
import useMyExpert from "@hooks/useMyExpert";
import useSanitizeHtml from "@hooks/useSanitizeHtml";
import { GetJourneyWithExpertQuery } from "@typing/Generated";
import { journeyRoute } from "@utils/routeUtils";

type Expert = NonNullable<GetJourneyWithExpertQuery["journey"]["expertRoles"]>[number]["expert"];

type Props = {
  expert: Expert;
  journeyId: string;
};

const ExpertInfoExpanded = ({ expert, journeyId }: Props) => {
  const myExpert = useMyExpert();

  const sanitizedBody = useSanitizeHtml(expert.bio, journeyId, expert);

  return (
    <div>
      <BackButton routerLink={journeyRoute({ journeyId, linkAfterJourney: "/expert", myExpert })} />
      <div className={styles.expandedContainer}>
        <div>
          <div className={styles.expertPurpleBox}>
            <div className="align-center">
              <ExpertAvatar className={styles.expertExpandedImg} expert={expert} />
            </div>
            <div className={styles.expertExpandedInfo}>
              <Heading2>
                <FormattedMessage id="pages.expertSection.hi" values={{ name: expert.firstName }} />
              </Heading2>
              <div className={styles.expertExpandedDescription}>
                <BodyCopy color="primary">{sanitizedBody}</BodyCopy>
              </div>
              <div className={styles.buttonFlex}>
                <SecondaryButton
                  routerLink={journeyRoute({ journeyId, linkAfterJourney: "/messages", myExpert })}
                  size="large"
                  textKey="pages.expertSection.message"
                />
                <ScheduleConsultButton journeyId={journeyId} messageKey="pages.expertSection.schedule" size="large" />
              </div>
            </div>
          </div>
        </div>
        <div className={styles.moreHelp}>
          <div className={styles.spacingBottomSmall}>
            <Heading2>
              <FormattedMessage id="pages.expertSection.howICanHelp" />
            </Heading2>
          </div>
          <div className={styles.spacingBottomSmall}>
            <div className={styles.spacingBottomSmall}>
              <BodyCopy bold color="primary">
                <FormattedMessage id="pages.expertSection.helpTopics.research.title" />
              </BodyCopy>
            </div>
            <BodyCopy color="primary">
              <ul>
                <li>
                  <FormattedMessage id="pages.expertSection.helpTopics.research.1" />
                </li>
                <li>
                  <FormattedMessage id="pages.expertSection.helpTopics.research.2" />
                </li>
                <li>
                  <FormattedMessage id="pages.expertSection.helpTopics.research.3" />
                </li>
                <li>
                  <FormattedMessage id="pages.expertSection.helpTopics.research.4" />
                </li>
              </ul>
            </BodyCopy>
          </div>
          <div className={styles.spacingBottomSmall}>
            <div className={styles.spacingBottomSmall}>
              <BodyCopy bold color="primary">
                <FormattedMessage id="pages.expertSection.helpTopics.plan.title" />
              </BodyCopy>
            </div>
            <BodyCopy color="primary">
              <ul>
                <li>
                  <FormattedMessage id="pages.expertSection.helpTopics.plan.1" />
                </li>
                <li>
                  <FormattedMessage id="pages.expertSection.helpTopics.plan.2" />
                </li>
                <li>
                  <FormattedMessage id="pages.expertSection.helpTopics.plan.3" />
                </li>
                <li>
                  <FormattedMessage id="pages.expertSection.helpTopics.plan.4" />
                </li>
              </ul>
            </BodyCopy>
          </div>
          <div className={styles.spacingBottomSmall}>
            <div className={styles.spacingBottomSmall}>
              <BodyCopy bold color="primary">
                <FormattedMessage id="pages.expertSection.helpTopics.schedule.title" />
              </BodyCopy>
            </div>
            <BodyCopy color="primary">
              <ul>
                <li>
                  <FormattedMessage id="pages.expertSection.helpTopics.schedule.1" />
                </li>
                <li>
                  <FormattedMessage id="pages.expertSection.helpTopics.schedule.2" />
                </li>
                <li>
                  <FormattedMessage id="pages.expertSection.helpTopics.schedule.3" />
                </li>
              </ul>
            </BodyCopy>
          </div>
          <div className={styles.spacingBottomSmall}>
            <div className={styles.spacingBottomSmall}>
              <BodyCopy bold color="primary">
                <FormattedMessage id="pages.expertSection.helpTopics.support.title" />
              </BodyCopy>
            </div>
            <BodyCopy color="primary">
              <ul>
                <li>
                  <FormattedMessage id="pages.expertSection.helpTopics.support.1" />
                </li>
                <li>
                  <FormattedMessage id="pages.expertSection.helpTopics.support.2" />
                </li>
                <li>
                  <FormattedMessage id="pages.expertSection.helpTopics.support.3" />
                </li>
              </ul>
            </BodyCopy>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpertInfoExpanded;
